import { toStringBigDecimal } from '@0xtorch/big-decimal'
import type { Transfer } from '@0xtorch/core'
import type { CostBasis } from '../../types/costBasis'
import type { Loan } from '../../types/loan'
import type { BorrowSubCategory, Transaction } from '../../types/transaction'
import { createAssetKey } from '../../utils'
import { mergeActionTransfers } from '../../utils/mergeActionTransfers'
import { createBlankTransaction } from './createBlankTransaction'
import { createBorrowTransaction } from './createBorrowTransaction'
import { createIncomeTransaction } from './createIncomeTransaction'
import { createTransactionsByTradeAction } from './createTransactionsByTradeAction'
import type {
  CreateTransactionsByCategoryActionParameters,
  CreateTransactionsByCategoryActionReturnTypes,
} from './types'

type CreateTransactionsByDepositActionParameters =
  CreateTransactionsByCategoryActionParameters & {
    readonly costBasis: CostBasis
    readonly handleLoanAsTrade?: boolean
    readonly use5PercentRule?: boolean
    readonly borrowSubCategory?: BorrowSubCategory
  }

type CreateTransactionsByDepositActionReturnTypes =
  CreateTransactionsByCategoryActionReturnTypes

export const createTransactionsByDepositAction = ({
  actionId,
  action,
  loans,
  crossActionBundles,
  assetBalances,
  assetBorrows,
  usedBalances,
  nftAcquiredTimestamps,
  costBasis,
  isLocked = false,
  handleLoanAsTrade = false,
  use5PercentRule = false,
  borrowSubCategory = 'bond',
}: CreateTransactionsByDepositActionParameters): CreateTransactionsByDepositActionReturnTypes => {
  // locked action の場合処理しない
  if (isLocked) {
    return {
      transactions: [],
      loans,
      crossActionBundles,
      assetBalances,
      assetBorrows,
      usedBalances,
    }
  }

  if (handleLoanAsTrade) {
    return createTransactionsByTradeAction({
      actionId,
      action,
      loans,
      crossActionBundles,
      assetBalances,
      assetBorrows,
      usedBalances,
      nftAcquiredTimestamps,
      isLocked,
      costBasis,
      use5PercentRule,
    })
  }

  if (action.loanId === undefined) {
    action.loanId = ''
  }

  // merge transfers
  const transfers = mergeActionTransfers(action.transfers)
  const transferCount: { all: number; in: number; out: number } = {
    all: 0,
    in: 0,
    out: 0,
  }
  const inTransfers: Transfer[] = []
  const outTransfers: Transfer[] = []
  for (const transfer of transfers) {
    transferCount.all += 1
    if (transfer.direction === 'in') {
      transferCount.in += 1
      inTransfers.push(transfer)
    }
    if (transfer.direction === 'out') {
      transferCount.out += 1
      outTransfers.push(transfer)
    }
  }

  let newAssetBalances = assetBalances
  let newAssetBorrows = assetBorrows
  let newUsedBalances = usedBalances
  const transactions: Transaction[] = []

  if (transferCount.all === 0) {
    const result = createBlankTransaction({
      actionId,
      timestamp: action.timestamp,
      assetBalances: newAssetBalances,
      assetBorrows: newAssetBorrows,
      usedBalances: newUsedBalances,
    })
    transactions.push(result.transaction)
    newAssetBalances = result.assetBalances
    newAssetBorrows = result.assetBorrows
    newUsedBalances = result.usedBalances
    return {
      transactions,
      loans,
      crossActionBundles,
      assetBalances: newAssetBalances,
      assetBorrows: newAssetBorrows,
      usedBalances: newUsedBalances,
    }
  }

  // update loan
  const loan: Loan = loans.get(action.loanId) ?? {
    id: action.loanId,
    borrows: new Map(),
    borrowsWithDebt: new Map(),
    deposits: new Map(),
    depositsWithBond: new Map(),
  }
  const bond =
    transferCount.in === 1
      ? {
          asset: createAssetKey(inTransfers[0].asset),
          amount: inTransfers[0].amount,
        }
      : undefined
  const positions = outTransfers.map((transfer) => ({
    asset: createAssetKey(transfer.asset),
    amount: transfer.amount,
  }))
  if (bond === undefined) {
    for (const position of positions) {
      const assetDeposits = loan.deposits.get(position.asset) ?? []
      assetDeposits.push(position)
      loan.deposits.set(position.asset, assetDeposits)
    }
  } else {
    const depositsWithBond = loan.depositsWithBond.get(bond.asset) ?? []
    depositsWithBond.push({
      positions,
      bond,
    })
    loan.depositsWithBond.set(bond.asset, depositsWithBond)
  }
  loans.set(action.loanId, loan)

  for (const transfer of inTransfers) {
    const result =
      transferCount.in === 1
        ? createBorrowTransaction({
            actionId,
            timestamp: action.timestamp,
            subCategory: borrowSubCategory,
            transfer,
            assetBalances: newAssetBalances,
            assetBorrows: newAssetBorrows,
            usedBalances: newUsedBalances,
          })
        : createIncomeTransaction({
            actionId,
            timestamp: action.timestamp,
            subCategory: 'other-income',
            transfer,
            assetBalances: newAssetBalances,
            assetBorrows: newAssetBorrows,
            usedBalances: newUsedBalances,
            costBasis,
          })
    transactions.push(result.transaction)
    newAssetBalances = result.assetBalances
    newAssetBorrows = result.assetBorrows
    newUsedBalances = result.usedBalances
  }

  for (const transfer of outTransfers) {
    // 預入に該当する out transfer は blank 取引を追加して、 asset 変化はさせない
    const result = createBlankTransaction({
      actionId,
      timestamp: action.timestamp,
      comment: `Deposit ${toStringBigDecimal(transfer.amount)} [${'symbol' in transfer.asset ? transfer.asset.symbol : (transfer.asset.name ?? 'Unknown NFT')}]`,
      assetBalances: newAssetBalances,
      assetBorrows: newAssetBorrows,
      usedBalances: newUsedBalances,
    })
    transactions.push(result.transaction)
    newAssetBalances = result.assetBalances
    newAssetBorrows = result.assetBorrows
    newUsedBalances = result.usedBalances
  }

  return {
    transactions,
    loans,
    crossActionBundles,
    assetBalances: newAssetBalances,
    assetBorrows: newAssetBorrows,
    usedBalances: newUsedBalances,
  }
}
